import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Input, Modal, Toast, type Column } from "@legal-500/legal500-design-system";
import type { ToastType } from "@legal-500/legal500-design-system";
import { DataTable } from "@/components/DataTable/DataTable";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import LoadingState from "@/components/LoadingState";
import { PageHeader } from "@/components/PageHeader";
import { PaginationControls } from "@/components/PaginationControls";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { useQuery } from "@/hooks/useApi";
import { usePermissions } from "@/hooks/usePermissions";
import { ROUTES } from "@/routes";
import {
  EVENT_SORT_FIELDS,
  eventsService,
  type EventSortDirection,
  type EventSortField,
  type StaffEvent,
} from "@/api/events.service";
import { EventFormModal } from "./EventFormModal";
import { isPastStartDate } from "./eventValidation";
import styles from "./Events.module.css";

const PAGE_SIZE = 25;

/** Sortable column keys, matched by DataTable against each column's `accessor`. */
const SORTABLE_COLUMN_KEYS: string[] = [...EVENT_SORT_FIELDS];

function isSortField(field: string): field is EventSortField {
  return (EVENT_SORT_FIELDS as readonly string[]).includes(field);
}

const STATUS_LABELS: Record<StaffEvent["status"], string> = {
  published: "Published",
  draft: "Draft",
  cancelled: "Cancelled",
};

/** HH:MM:SS from content-api; staff read HH:MM. */
function formatTime(startTime: string): string {
  return startTime.slice(0, 5);
}

export function EventsListPage() {
  const { hasPermission } = usePermissions();
  const canRead = hasPermission("cms.events.read");
  const canWrite = hasPermission("cms.events.write");
  const canDelete = hasPermission("cms.events.delete");

  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  // null means "no sort chosen" — the API then applies its default upcoming-then-past order.
  const [sortField, setSortField] = useState<EventSortField | null>(null);
  const [sortDirection, setSortDirection] = useState<EventSortDirection>("asc");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<StaffEvent | null>(null);
  const [deleting, setDeleting] = useState<StaffEvent | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  // Debounced from the change handler rather than an effect on `searchInput`: an effect also
  // runs on mount, and its timer would then reset the page 250ms later — silently dragging a
  // reader who had already paged on back to page 1. Only an actual keystroke may reset the page.
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    },
    [],
  );

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setQuery(value.trim());
      // A new query re-filters the whole catalogue, so page 3 of the old results is
      // meaningless — start again at the first page. Batched with the query above, so the
      // list is fetched once, at offset 0.
      setPage(1);
    }, 250);
  };

  const offset = (page - 1) * PAGE_SIZE;

  // The STAFF list: every event regardless of status or start date. Past, draft and
  // cancelled events stay here so staff can see and correct them — only the public site
  // hides them (event-management spec, "Event status controls public visibility").
  //
  // Paging AND sorting are both server-side. Sorting the 25 loaded rows client-side would
  // order only the current page and quietly lie about where the earliest — or the
  // alphabetically first — event is.
  const fetchEvents = useCallback(
    () =>
      eventsService.listForStaff({
        q: query || undefined,
        limit: PAGE_SIZE,
        offset,
        ...(sortField ? { sortField, sortDirection } : {}),
      }),
    [query, offset, sortField, sortDirection],
  );
  const { data, loading, error, refetch } = useQuery(fetchEvents, [
    query,
    offset,
    sortField,
    sortDirection,
  ]);

  if (!canRead) {
    return <UnauthorizedView />;
  }

  const events = data?.events ?? [];
  const total = data?.pagination.total ?? events.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /**
   * Cycles a column asc → desc → unsorted, the pattern the other sortable staff lists use.
   * Dropping back to unsorted is what restores the default upcoming-then-past order.
   */
  const handleSort = (field: string) => {
    if (!isSortField(field)) return;
    setPage(1);
    if (field !== sortField) {
      setSortField(field);
      setSortDirection("asc");
    } else if (sortDirection === "asc") {
      setSortDirection("desc");
    } else {
      setSortField(null);
      setSortDirection("asc");
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (event: StaffEvent) => {
    setEditing(event);
    setFormOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setRemoving(true);
    setDeleteError(null);
    try {
      await eventsService.remove(deleting.id);
      setToast({ message: `"${deleting.title}" deleted.`, type: "success" });
      setDeleting(null);
      // Deleting the only row on the last page would otherwise strand the reader on an empty
      // page; stepping back re-fetches through the dependency change.
      if (events.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        refetch();
      }
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "The event could not be deleted");
    } finally {
      setRemoving(false);
    }
  };

  const columns: Column<StaffEvent>[] = [
    {
      header: "Title",
      accessor: "title",
      wrap: true,
      render: (_value, row) => (
        <div>
          <span className={styles.rowTitle}>{row.title}</span>
          {isPastStartDate(row.startDate) && <span className={styles.pastBadge}>Past</span>}
        </div>
      ),
    },
    { header: "Type", accessor: "type" },
    {
      header: "Starts",
      accessor: "startDate",
      render: (_value, row) => (
        <span>
          {row.startDate} {formatTime(row.startTime)} ({row.timezone})
        </span>
      ),
    },
    {
      header: "Location",
      accessor: "locationShort",
      wrap: true,
      render: (_value, row) => <span>{row.locationShort ?? "—"}</span>,
    },
    {
      header: "Status",
      accessor: "status",
      render: (_value, row) => (
        <span className={styles[`status_${row.status}`]}>{STATUS_LABELS[row.status]}</span>
      ),
    },
    {
      header: "Actions",
      accessor: "id",
      render: (_value, row) => (
        <div className={styles.rowActions}>
          <Button
            type="button"
            size="small"
            variant="outlined"
            color="default"
            disabled={!canWrite}
            aria-label={`Edit ${row.title}`}
            onClick={() => openEdit(row)}
          >
            Edit
          </Button>
          <Button
            type="button"
            size="small"
            variant="outlined"
            color="error"
            disabled={!canDelete}
            aria-label={`Delete ${row.title}`}
            onClick={() => {
              setDeleteError(null);
              setDeleting(row);
            }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Events"
        subtitle="Every event Events Media maintain — upcoming first, then past. Draft and cancelled events stay here; only the public site hides them."
      >
        <div className={styles.headerActions}>
          <Link className={styles.bannerLink} to={ROUTES.events.heroBanner}>
            Edit hero banner
          </Link>
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={openCreate}
            disabled={!canWrite}
          >
            New event
          </Button>
        </div>
      </PageHeader>

      <div className={styles.toolbar}>
        <Input
          label="Search events"
          value={searchInput}
          placeholder="Title, short location or venue address"
          onChange={e => handleSearchChange(e.target.value)}
        />
        <span className={styles.count}>
          {total} event{total === 1 ? "" : "s"}
        </span>
      </div>

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={loading} />}

      {loading ? (
        <LoadingState message="Loading events…" />
      ) : (
        <DataTable
          data={events}
          columns={columns}
          rowKey="id"
          emptyMessage="No events yet. Create one to get started."
          sortableColumnKeys={SORTABLE_COLUMN_KEYS}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      )}

      <PaginationControls
        currentPage={page}
        totalPages={totalPages}
        startIndex={offset}
        endIndex={offset + PAGE_SIZE}
        totalCount={total}
        itemName={total === 1 ? "event" : "events"}
        onPageChange={setPage}
      />

      {formOpen && (
        <EventFormModal
          // Remount per event, so the form's initial state is the record being edited.
          key={editing?.id ?? "new"}
          isOpen={formOpen}
          event={editing}
          onClose={() => setFormOpen(false)}
          onSaved={message => {
            setFormOpen(false);
            setToast({ message, type: "success" });
            refetch();
          }}
        />
      )}

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Delete event"
        size="small"
      >
        <p>
          Delete "{deleting?.title}"? It is removed from the staff list and the public site
          immediately.
        </p>
        {deleteError && (
          <p className={styles.error} role="alert">
            {deleteError}
          </p>
        )}
        <div className={styles.formActions}>
          <Button
            type="button"
            variant="outlined"
            color="default"
            onClick={() => setDeleting(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={removing}
          >
            {removing ? "Deleting…" : "Delete event"}
          </Button>
        </div>
      </Modal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default EventsListPage;
